import { Line } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import { useProjectActions, useProjectContext } from "@contexts/projects";
import { useEffect, useMemo } from "react";
import { formatDateToNumberMMYY } from "@utils/formatDate";
import { useAuthContext } from "@contexts/auth";
import AnnulmentIndicator from "@components/titleAnnulments/AnnulmentIndicator";

Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)


const ProjectLineChart = () => {
  const { activeProject: { followUps = [], cashFlowPlanning, titleAnnulments }, selectedGraph } = useProjectContext();
  const { selectXlsxData } = useProjectActions()
  const { user: { configurations } } = useAuthContext();
  const { financial: financialConfig = [] } = configurations.dashboard.graphs
  // Sem previsto/medido/físico-financeiro (`physicalFinancial.available`, decidido pelo
  // BE a partir da série completa da obra), o gráfico não tem o que desenhar -- um
  // gráfico "Avanço Físico Financeiro" sem nenhuma linha pareceria quebrado, não
  // ausente (serve-works-without-optional-sources, D5).
  const physicalFinancialAvailable = cashFlowPlanning?.physicalFinancial?.available === true;


  const { base, measured, baseline, scheduled, dates } = useMemo(() => followUps.reduce((accumulator, followUp) => {
    accumulator.base.push(followUp.base != null ? followUp.base * 100 : undefined)
    accumulator.measured.push(followUp.measured != null ? followUp.measured * 100 : undefined)
    accumulator.baseline.push(followUp.baseline != null ? followUp.baseline * 100 : undefined)
    accumulator.scheduled.push(followUp.scheduled != null ? followUp.scheduled * 100 : undefined)
    accumulator.dates.push(formatDateToNumberMMYY(followUp.date))
    return accumulator
  }, {
    base: [],
    measured: [],
    baseline: [],
    scheduled: [],
    dates: [],
  }), [followUps])

  useEffect(() => {
    const fields = [
      { label: 'Data', key: 'date' },
      { label: 'Medido', key: 'measured' },
      { label: 'Linha de base', key: 'base' },
      { label: 'Previsto', key: 'scheduled' },
    ]
    const resources = dates.map((date, index) => ({
      date,
      base: base[index],
      measured: measured[index],
      scheduled: scheduled[index],
      baseline: baseline[index]
    }));

    selectXlsxData({ fields, resources })

  }, [selectedGraph, dates, base, measured, scheduled, baseline])

  const checkGraphConfig = (e) => {
    switch (e) {
      case 'measured':
        return {
          label: "Medido",
          borderColor: "#3acff7",
          backgroundColor: "#3acff7",
          borderWidth: 3,
          data: measured,
        }
      case 'scheduled':
        return {
          label: "Previsto",
          borderColor: "#FFA07A",
          backgroundColor: "#FFA07A",
          borderWidth: 3,
          data: scheduled,
        }
      case 'base':
        return {
          label: "Linha de base",
          borderColor: "#FFD700",
          backgroundColor: "#FFD700",
          borderWidth: 3,
          borderDash: [6, 4],
          data: base,
        }
      default:
        break;
    }
  }

  // Marca vertical "Anulação de título a partir da medição N". Só é desenhada no
  // follow-up cujo ano-mês (UTC) é o de `fromMeasurementDate`; sem correspondência exata
  // (data ausente ou mês fora da série filtrada), não desenha nada -- nunca adivinha.
  const fromMeasurement = titleAnnulments?.count > 0 ? titleAnnulments.fromMeasurement : null;
  const fromMeasurementDate = titleAnnulments?.count > 0 ? titleAnnulments.fromMeasurementDate : null;
  const annulmentPlugins = useMemo(() => {
    if (fromMeasurement == null || !Number.isFinite(Number(fromMeasurement))) return [];
    if (typeof fromMeasurementDate !== "string" || !/^\d{4}-\d{2}/.test(fromMeasurementDate)) return [];
    const target = fromMeasurementDate.slice(0, 7);
    const index = followUps.findIndex((f) => {
      if (!f?.date) return false;
      const d = new Date(f.date);
      if (Number.isNaN(d.getTime())) return false;
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}` === target;
    });
    if (index < 0) return [];
    const n = Number(fromMeasurement);
    return [{
      id: "annulmentMarker",
      index,
      label: `Anulação de título a partir da medição ${n}`,
      afterDatasetsDraw(chart) {
        const x = chart?.scales?.x?.getPixelForValue?.(index);
        const area = chart?.chartArea;
        const ctx = chart?.ctx;
        if (x == null || !area || !ctx) return;
        ctx.save();
        ctx.strokeStyle = "#d9534f";
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.moveTo(x, area.top);
        ctx.lineTo(x, area.bottom);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = "#d9534f";
        ctx.font = "11px sans-serif";
        ctx.textAlign = x > (area.left + area.right) / 2 ? "right" : "left";
        ctx.fillText(`Anulação de título a partir da medição ${n}`, x + (x > (area.left + area.right) / 2 ? -4 : 4), area.top + 12);
        ctx.restore();
      },
    }];
  }, [fromMeasurement, fromMeasurementDate, followUps]);

  if (!physicalFinancialAvailable) return null;

  return (
    <>
    <Line plugins={annulmentPlugins} options={{
      responsive: true,
      maintainAspectRatio: false,
      // aspectRatio: 1|3,
      height: 500,
      plugins: {
        legend: {
          display: true,
          position: "top",
          align: "center",
        },
        datalabels: {
          display: false
        },
        title: {
          display: false,
          text: "Avanço Físico Financeiro",
          align: "start",
          color: "black",
          font: {
            size: "18px"
          }
        }
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Mês/Ano',
            font: {
              weight: 'bold'
            }
          }
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'Porcentagem',
            font: {
              weight: 'bold'
            }
          }
        }
      }
    }}
      data={{
        labels: dates,
        datasets: financialConfig.map(i => {
          return checkGraphConfig(i)
        }).filter(Boolean),
      }} />
    <AnnulmentIndicator summary={titleAnnulments} kind="both" placement="chart" />
    </>
  )
}

export default ProjectLineChart;
